import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { columns as dcConnectionColumns } from "./DCConnectionsData";

import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import {
	getAllConnections,
	getConnectionById,
	addConnection,
	updateConnection,
	deleteDCConnection,
} from "./request/request";
import { getHardwareListByDataCenter } from "../Port/request/request";
import { getPortsByHardwareId } from "../Hardware/request/request";
import { getDataCentersIdsNames } from "../Hardware/request/request";

// ---------- Interfaces -----------
interface DCConnection {
	subId: string;
	id: number;
	connectionType: "L2L" | "IPX";
	datacenterIdSource: string;
	hardwareIdSource: number | null;
	portIdSource: number | null;
	capacityMbps: number;
	datacenterSource?: string | null;
	datacenterIdDest?: string;
	hardwareIdDest?: number | null;
	hardwareSource?: string | { hostname: string } | null;
	hardwareDest?: string | { hostname: string } | null;
	datacenterDest?: string | { name: string } | null;
	portIdDest?: number | null;
	metadata?: object;
}
interface DCConnectionPayload {
	subId: string;
	connectionType: "L2L" | "IPX";
	datacenterIdSource: string;
	hardwareIdSource: number | null;
	portIdSource: number | null;
	capacityMbps: number;
	datacenterSource?: string | null;
	datacenterIdDest?: string;
	hardwareIdDest?: number | null;
	portIdDest?: number | null;
	metadata?: object;
}

interface HardwareItem {
	id: number;
	hostname: string;
}

interface PortItem {
	id: number;
	portName: string;
	portNumber: string;
}

interface DataCenterItem {
	datacenterId: string; // or number
	name: string;
}

export default function DCConnectionsPage() {
	const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

	// 1) State for table data
	const [connections, setConnections] = useState<DCConnection[]>([]);
	const [isFetching, setIsFetching] = useState(false);
	const [dropdownConnection, setDropdownConnection] = useState<
		"All" | "IPX" | "L2L"
	>("All");

	// 2) Add/Edit modal
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);

	// Fields for Add/Edit
	const [connectionId, setConnectionId] = useState<number | null>(null);
	const [connectionType, setConnectionType] = useState<"L2L" | "IPX">("IPX");
	const [dcSourceId, setDcSourceId] = useState<string>("");
	const [hwSourceId, setHwSourceId] = useState<number | null>(null);
	const [portSourceId, setPortSourceId] = useState<number | null>(null);
	const [dcDestId, setDcDestId] = useState<string>("");
	const [hwDestId, setHwDestId] = useState<number | null>(null);
	const [portDestId, setPortDestId] = useState<number | null>(null);
	const [capacityMbps, setCapacityMbps] = useState<string>("");
	const [connectionSubId, setConnectionSubId] = useState<string>("");

	// 3) Data center, hardware, port dropdown arrays
	const [dcOptions, setDcOptions] = useState<string[]>([]);
	const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
	const [hwSourceOptions, setHwSourceOptions] = useState<string[]>([]);
	const [portSourceOptions, setPortSourceOptions] = useState<string[]>([]);

	//States for L2L connections
	const [hwDestOptions, setHwDestOptions] = useState<string[]>([]);
	const [portDestOptions, setPortDestOptions] = useState<string[]>([]);

	//States that handle dropdown values
	const [portSourceList, setPortSourceList] = useState<PortItem[]>([]);
	const [hwSourceList, setHwSourceList] = useState<HardwareItem[]>([]);
	const [portDestList, setPortDestList] = useState<PortItem[]>([]);
	const [hwDestList, setHwDestList] = useState<HardwareItem[]>([]);
	// 4) Delete confirmation
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [connectionToDelete, setConnToDelete] = useState<DCConnection | null>(
		null
	);

	// 7) View modal state
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedConnection, setSelectedConnection] =
		useState<DCConnection | null>(null);

	useEffect(() => {
		if (isLoading || !isAuthenticated) return;
		(async () => {
			try {
				setIsFetching(true);
				const token = await getAccessTokenSilently();

				// A) Fetch all connections
				const resp = await getAllConnections(token);
				console.log(resp.data);
				setConnections(resp.data); // assume resp.data is DCConnection[]
				const dcResp = await getDataCentersIdsNames(token);
				// The endpoint might return an array of objects with many fields
				// Shape/filter each object to { datacenterId, name }
				const shapedData: DataCenterItem[] = dcResp.data.map(
					(dc: DataCenterItem) => ({
						datacenterId: dc.datacenterId,
						name: dc.name,
					})
				);
				setDataCenters(shapedData);
				const dcNames = dcResp.data.map((dc) => dc.name);
				setDcOptions(dcNames);
			} catch (err) {
				console.error("Error fetching data:", err);
			} finally {
				setIsFetching(false);
			}
		})();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	// ------------- Handler: Add -------------
	function handleAddConnection() {
		setEditMode(false);
		// Clear fields
		setConnectionId(null);
		setConnectionType("IPX");
		setDcSourceId("");
		setHwSourceId(null);
		setPortSourceId(null);
		setDcDestId("");
		setHwDestId(null);
		setPortDestId(null);
		setCapacityMbps("");
		setIsModalOpen(true);
	}

	const handleViewConnection = async (connection: DCConnection) => {
		try {
			const token = await getAccessTokenSilently();
			const response = await getConnectionById(token, connection.id);
			setSelectedConnection(response.data);
			setIsViewModalOpen(true);
		} catch (error) {
			console.error("Error viewing port:", error);
		}
	};
	// ------------- Handler: Edit -------------
	async function handleEditConnection(connection: DCConnection) {
		setEditMode(true);
		setIsModalOpen(true);
		setConnectionId(connection.id);
		setConnectionType(connection.connectionType);
		setDcSourceId(connection.datacenterIdSource);
		setHwSourceId(connection.hardwareIdSource);
		setPortSourceId(connection.portIdSource || null);
		setDcDestId(connection.datacenterIdDest || "");
		setHwDestId(connection.hardwareIdDest || null);
		setPortDestId(connection.portIdDest || null);
		setCapacityMbps(String(connection.capacityMbps));
	}

	// ------------- Handler: Save (Add/Update) -------------
	async function handleSaveConnection() {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			// Build payload
			const payload: DCConnectionPayload = {
				connectionType,
				datacenterIdSource: dcSourceId,
				capacityMbps: Number(capacityMbps),
				hardwareIdSource: hwSourceId,
				portIdSource: portSourceId,
				subId: connectionSubId,
			};

			if (connectionType === "L2L") {
				payload.datacenterIdDest = dcDestId;
				payload.hardwareIdDest = hwDestId;
				payload.portIdDest = portDestId;
			}

			if (editMode && connectionId !== null) {
				await updateConnection(token, connectionId, payload);
				setConnections((prev) =>
					prev.map((connection) =>
						connection.id === connectionId
							? { ...connection, ...payload }
							: connection
					)
				);
			} else {
				await addConnection(token, payload);
				const allResp = await getAllConnections(token);
				setConnections(allResp.data);
			}
		} catch (err) {
			console.error("Error saving connection:", err);
		} finally {
			setIsFetching(false);
			setIsModalOpen(false);
		}
	}

	// ------------- Handler: Delete -------------
	function handleDeleteConnection(connection: DCConnection) {
		setConnToDelete(connection);
		setIsAlertOpen(true);
	}

	async function confirmDeleteConnection() {
		if (!connectionToDelete) return;
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			await deleteDCConnection(token, connectionToDelete.id);
			setConnections((prev) =>
				prev.filter((c) => c.id !== connectionToDelete.id)
			);
		} catch (err) {
			console.error("Error deleting connection:", err);
		} finally {
			setIsFetching(false);
			setIsAlertOpen(false);
			setConnToDelete(null);
		}
	}

	function handleConnectionTypeSelect(newValue: string) {
		setDropdownConnection(newValue as "All" | "IPX" | "L2L");
	}

	// ------------- Data Center/Hardware/Port Logic for Modal -------------
	async function handleDcSelect(dcName: string, side: "source" | "dest") {
		const matchedDc = dataCenters.find((dc) => dc.name === dcName);
		if (!matchedDc) {
			console.error("No data center found for name:", dcName);
			return;
		}

		if (side === "source") {
			setDcSourceId(matchedDc.datacenterId);
			setHwSourceId(null);
			setPortSourceId(null);
		} else {
			setDcDestId(matchedDc.datacenterId);
			setHwDestId(null);
			setPortDestId(null);
		}

		try {
			const token = await getAccessTokenSilently();
			const hwResp = await getHardwareListByDataCenter(
				token,
				matchedDc.datacenterId
			);
			const hwNames = hwResp.data.map((hw: HardwareItem) => hw.hostname);

			if (side === "source") {
				setHwSourceList(hwResp.data);
				setHwSourceOptions(hwNames);
			} else {
				setHwDestList(hwResp.data);
				setHwDestOptions(hwNames);
			}
		} catch (err) {
			console.error("Error fetching hardware for source DC:", err);
		}
	}

	async function handleHwSelect(hwName: string, side: "source" | "dest") {
		const hwList = side === "source" ? hwSourceList : hwDestList;
		const matchedHw = hwList.find((hw) => hw.hostname === hwName);
		if (!matchedHw) {
			console.error("No hardware found for name:", hwName);
			return;
		}

		if (side === "source") {
			setHwSourceId(matchedHw.id);
			setPortSourceId(null);
		} else {
			setHwDestId(matchedHw.id);
			setPortDestId(null);
		}

		try {
			const token = await getAccessTokenSilently();
			const portsResp = await getPortsByHardwareId(
				token,
				matchedHw.id.toString()
			); //Parse to string
			const portNames = portsResp.data.ports.map(
				(p: PortItem) => `${p.portNumber} - ${p.portName}`
			);

			if (side === "source") {
				setPortSourceList(portsResp.data.ports);
				setPortSourceOptions(portNames);
			} else {
				setPortDestList(portsResp.data.ports);
				setPortDestOptions(portNames);
			}
		} catch (err) {
			console.error("Error fetching ports for source hardware:", err);
		}
	}

	function handlePortSelect(portLabel: string, side: "source" | "dest") {
		const portList = side === "source" ? portSourceList : portDestList;
		const match = portList.find(
			(p) => `${p.portNumber} - ${p.portName}` === portLabel
		);
		if (!match) {
			console.error("No port found for label:", portLabel);
			return;
		}
		if (side === "source") {
			setPortSourceId(match.id);
		} else {
			setPortDestId(match.id);
		}
	}

	// ----------------------------- HELPERS -----------------------------------------

	function getDCNameById(datacenterId: string | null): string {
		if (!datacenterId) return "";
		const match = dataCenters.find((dc) => dc.datacenterId === datacenterId);
		return match ? match.name : "";
	}
	function getHwNameById(hardwareId: number | null): string {
		if (!hardwareId) return "";
		const match = hwSourceList.find((hw) => hw.id === hardwareId);
		return match ? match.hostname : "";
	}
	function getPortNamebyId(portId: number | null): string {
		if (!portId) return "";
		const match = portSourceList.find((p) => p.id === portId);
		return match ? `${match.portNumber} - ${match.portName}` : "";
	}
	function getHwDestNameById(hwId: number | null): string {
		if (!hwId) return "";
		const match = hwDestList.find((hw) => hw.id === hwId);
		return match ? match.hostname : "";
	}

	function getPortDestNameById(portId: number | null): string {
		if (!portId) return "";
		const match = portDestList.find((p) => p.id === portId);
		return match ? `${match.portNumber} - ${match.portName}` : "";
	}

	// ------------------------------------------------------------------------------------
	// Override "Actions" column
	// ------------------------------------------------------------------------------------
	const columns = dcConnectionColumns.map((col) => {
		if (col.header === "Actions" && col.renderCell) {
			return {
				...col,
				renderCell: (row: DCConnection) => (
					<div className="flex items-center gap-2">
						{/* View */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleViewConnection(row)}
						>
							<IconEye className="w-6 h-6" />
						</button>

						{/* Edit */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleEditConnection(row)}
						>
							<IconEdit className="w-10 h-10" />
						</button>

						{/* Delete */}
						<button
							className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
							onClick={() => handleDeleteConnection(row)}
						>
							<IconDelete className="w-6 h-6" />
						</button>
					</div>
				),
			};
		}
		return col;
	});

	return (
		<div className="p-4 relative">
			{/* Header */}

			<div className="mb-4 p-4 bg-[var(--semantic-bg-primary)] border border-semantic-border-secondary rounded-sm">
				<div className="flex items-end justify-between">
					{/* Left: Datacenter + Hardware dropdowns */}
					<div className="flex items-end space-x-4">
						{/* 1) Datacenter */}
						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
								Select Connection Type
							</label>
							<GenericDropdown
								items={["All", "IPX", "L2L"]}
								defaultLabel="Choose Connection..."
								value={dropdownConnection}
								onSelect={handleConnectionTypeSelect}
								minWidthClass="min-w-[200px]"
								maxWidthClass="max-w-[240px]"
							/>
						</div>
					</div>

					{/* Right: Add new port button */}

					<GenericButton
						label={
							<div className="flex items-center gap-2">
								<IconPlus className="w-4 h-4" />
								<span>Add Connection</span>
							</div>
						}
						variant="brand"
						size="md"
						onClick={handleAddConnection}
					/>
				</div>
			</div>

			{/* Table */}
			<GenericTable
				columns={columns}
				data={connections}
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				width="100%"
			/>

			{/* Add/Edit Modal */}
			<GenericModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={editMode ? "Edit Connection" : "Add Connection"}
				size="lg"
			>
				<div className="grid grid-cols-2 gap-4 mb-4">
					{/* Connection Type */}
					<div>
						<label className="text-sm font-medium">Connection Type</label>
						<GenericDropdown
							items={["L2L", "IPX"]}
							value={connectionType}
							onSelect={(val: string) =>
								setConnectionType(val as "L2L" | "IPX")
							}
						/>
					</div>
					{/* Source Data Center */}
					<div>
						<label className="text-sm font-medium">Source DC</label>
						<GenericDropdown
							items={dcOptions}
							value={dcSourceId ? getDCNameById(dcSourceId) : ""}
							onSelect={(val) => handleDcSelect(val, "source")}
							defaultLabel="Select DC"
						/>
					</div>
					{/* Source Hardware */}
					<div>
						<label className="text-sm font-medium">Hardware (Source)</label>
						<GenericDropdown
							items={hwSourceOptions}
							value={hwSourceId ? getHwNameById(hwSourceId) : ""}
							onSelect={(vale) => handleHwSelect(vale, "source")}
							defaultLabel="Select HW"
						/>
					</div>
					{/* Source Port */}
					<div>
						<label className="text-sm font-medium">Port (Source)</label>
						<GenericDropdown
							items={portSourceOptions}
							value={portSourceId ? getPortNamebyId(portSourceId) : ""}
							onSelect={(val) => handlePortSelect(val, "source")}
							defaultLabel="Select Port"
						/>
					</div>
					{/* capacityMbps */}
					<div>
						<GenericInput
							label="Capacity (Mbps)"
							value={capacityMbps}
							onChange={setCapacityMbps}
						/>
					</div>
					<div>
						<GenericInput
							label="subId"
							value={connectionSubId}
							onChange={setConnectionSubId}
						/>
					</div>
					{/* Destination fields only if L2L */}
					{connectionType === "L2L" && (
						<>
							<div>
								<label className="text-sm font-medium">
									Destination Datacenter
								</label>
								<GenericDropdown
									items={dcOptions}
									value={dcDestId}
									onSelect={(val) => handleDcSelect(val, "dest")}
									defaultLabel="Select DC"
								/>
							</div>
							<div>
								<label className="text-sm font-medium">
									Hardware (Destination)
								</label>
								<GenericDropdown
									items={hwDestOptions}
									value={hwDestId ? getHwDestNameById(hwDestId) : ""}
									onSelect={(val) => handleHwSelect(val, "dest")}
									defaultLabel="Select HW"
								/>
							</div>
							<div>
								<label className="text-sm font-medium">
									Port (Destination)
								</label>
								<GenericDropdown
									items={portDestOptions}
									value={portDestId ? getPortDestNameById(portDestId) : ""}
									onSelect={(val) => handlePortSelect(val, "dest")}
									defaultLabel="Select Port"
								/>
							</div>
						</>
					)}
				</div>
				<div className="flex justify-end gap-2">
					<GenericButton
						label="Cancel"
						variant="secondary"
						onClick={() => setIsModalOpen(false)}
					/>
					<GenericButton
						label="Save"
						variant="brand"
						onClick={handleSaveConnection}
					/>
				</div>
			</GenericModal>

			<GenericModal
				isOpen={isViewModalOpen}
				onClose={() => setIsViewModalOpen(false)}
				title="View Connection Details"
				size="md"
			>
				{selectedConnection && (
					<div className="grid grid-cols-2 gap-4 mb-4">
						{/* Connection Type */}
						<GenericInput
							label="Connection Type"
							value={selectedConnection.connectionType}
							readOnly
						/>

						{/* Source Data Center */}
						<GenericInput
							label="Source DataCenter"
							value={selectedConnection.datacenterIdSource}
							readOnly
						/>

						{/* Source Hardware */}
						<GenericInput
							label="Source Hardware ID"
							value={String(selectedConnection.hardwareIdSource)}
							readOnly
						/>

						{/* Source Port */}
						<GenericInput
							label="Source Port ID"
							value={String(selectedConnection.portIdSource)}
							readOnly
						/>

						{/* Capacity */}
						<GenericInput
							label="Capacity (Mbps)"
							value={String(selectedConnection.capacityMbps)}
							readOnly
						/>

						{/* If it's L2L, also show the destination fields */}
						{selectedConnection.connectionType === "L2L" && (
							<>
								<GenericInput
									label="Dest DataCenter"
									value={selectedConnection.datacenterIdDest || ""}
									readOnly
								/>
								<GenericInput
									label="Dest Hardware ID"
									value={
										selectedConnection.hardwareIdDest
											? String(selectedConnection.hardwareIdDest)
											: ""
									}
									readOnly
								/>
								<GenericInput
									label="Dest Port ID"
									value={
										selectedConnection.portIdDest
											? String(selectedConnection.portIdDest)
											: ""
									}
									readOnly
								/>
							</>
						)}
					</div>
				)}

				<div className="flex justify-end">
					<GenericButton
						label="Close"
						variant="secondary"
						onClick={() => setIsViewModalOpen(false)}
					/>
				</div>
			</GenericModal>

			{/* Delete Confirmation */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={() => setIsAlertOpen(false)}
				onConfirm={confirmDeleteConnection}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the connection."
				cancelLabel="Cancel"
				confirmLabel="Delete"
			/>

			{/* Spinner */}
			{isFetching && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
				</div>
			)}
		</div>
	);
}
